/**
 * Utility functions for manipulating page elements.
 */

// import { useDateFormat } from "@vueuse/core";
// import DOMPurify from 'dompurify';

/**
 * Focuses on the first invalid input element on the page.
 */
export const focusFirstInvalidInput = () => {
    const invalidInput = document.querySelector('.is-invalid') || document.querySelector('.p-invalid');
    if (invalidInput) {
        invalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        invalidInput.focus();
    }
};


/**
 * Focuses on the error message element on the page by scrolling it into view.
 * The function searches for an element with the ID `error-msg` and, if found,
 * ensures it is brought into the user's viewport.
 */
export const focusErrorMessage = () => {
    const errorElement = document.querySelector('#error-msg') || document.querySelector('.p-message.p-message-error');
    if (errorElement) {
        scrollIntoView(errorElement);
    }
};

export const focusFirstEnabledInput = (containerId) => {
    if (!containerId) return;

    const container = document.getElementById(containerId);
    if (!container) return;

    const firstEnabledInputs = container.querySelectorAll(
        'input:not([disabled]):not([readonly]), ' +
        'textarea:not([disabled]):not([readonly]), ' +
        'select:not([disabled]), ' +
        '.p-select-label:not([disabled]), ' +
        '.p-datepicker-input:not([disabled]), ' +
        '.ql-editor[contenteditable="true"]'
    );
    
    const firstEnabledInput = firstEnabledInputs[0];
    if (firstEnabledInput) {
        if (firstEnabledInput.classList.contains('ql-editor')) {
            // Focus the Quill editor
            firstEnabledInput.focus();
            
            // Place cursor at the start of the editor
            const range = document.createRange();
            const selection = window.getSelection();
            
            // Get the Quill instance to set formatting
            const quillInstance = firstEnabledInput.__quill;
            
            if (firstEnabledInput.firstChild) {
                range.setStart(firstEnabledInput.firstChild, 0);
                range.collapse(true);
                selection?.removeAllRanges();
                selection?.addRange(range);
            } else {
                // If editor is empty, create a text node with formatting
                const textNode = document.createTextNode('');
                firstEnabledInput.appendChild(textNode);
                range.setStart(textNode, 0);
                range.collapse(true);
                selection?.removeAllRanges();
                selection?.addRange(range);
            }
            
            // Apply default formatting if Quill instance is available
            if (quillInstance) {
                quillInstance.format('font', 'helvetica');
                quillInstance.format('size', '16px');
            }
        } else {        
            firstEnabledInput.focus();
            firstEnabledInput.select?.();
        }
    }
};

/**
 * Scrolls the given element into view.
 * @param {Element} element - The DOM element to scroll into view.
 */
export const scrollIntoView = (element) => {
    if (!element) return;

    element.scrollIntoView({ behavior: 'smooth' });
};

/**
 * Scrolls the page to the top.
 */
export const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

/**
 * Sets focus on the element identified by the given selector.
 * @param {string} selector - The ID or inputId attribute of the element to focus.
 */
export const setFocus = (selector) => {
    if (!selector) return;
    const element = getElementById(selector);
    if (element) {
        element.focus();
        element?.select?.();
    } 
};

export const getElementById = (id) => {
    if (!id) return null;

    // Try to find by ID first
    let element = document.getElementById(id);

    // If not found, try inputId attribute  
    if (!element) {
        element = document.querySelector(`[inputId="${id}"]`);
    }

    if (!element) return null;

    // Check if the element itself is focusable
    const isFocusable = element.hasAttribute('tabindex') || 
                       ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(element.tagName) ||
                       element.classList.contains('ql-editor');

    if (isFocusable) {
        return element;
    }

    // Try to find a focusable child element (for PrimeVue components)
    const focusableChild = element.querySelector(
        '[tabindex]:not([tabindex="-1"]), ' +
        'input:not([disabled]), ' +
        'textarea:not([disabled]), ' +
        'select:not([disabled]), ' +
        'button:not([disabled]), ' +
        'a[href], ' +
        '.p-select-label[tabindex="0"], ' +
        '.ql-editor[contenteditable="true"]'
    );

    return focusableChild || element;
};

/**
 * Returns the head title by appending the provided title with the application title.
 * @param {string} title - The title to be appended.
 * @returns {string} The head title.
 */
export const getHeadTitle = (title) => {
    return title + ' - ' + import.meta.env.VITE_APP_TITLE;
};

/**
 * Delays execution for the specified number of milliseconds.
 * @param {number} ms - The number of milliseconds to delay.
 * @returns {Promise<void>} A promise that resolves after the delay.
 */
export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
